import BackButton from '../components/BackButton';
import { useApp } from '../App';
import { useState, useEffect } from 'react';
import api from '../api';
import { startImpersonation } from '../utils/impersonation';
import Loading from '../components/Loading';
import { useI18n } from '../i18n/index.jsx';

export default function AdminClients() {
  const { user } = useApp();
  const { t } = useI18n();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientBalance, setClientBalance] = useState(null);
  const [clientTransactions, setClientTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [msg, setMsg] = useState('');

  const isSuperadmin = user?.role === 'superadmin';

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    filterAndSortClients();
  }, [clients, searchTerm, sortBy, sortOrder]);

  async function loadClients() {
    try {
      const response = await api.get('/admin/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
      setMsg('❌ Ошибка загрузки клиентов');
    }
    setLoading(false);
  }

  function filterAndSortClients() {
    let filtered = [...clients];
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(client => 
        (client.first_name?.toLowerCase() || '').includes(term) ||
        (client.last_name?.toLowerCase() || '').includes(term) ||
        (client.telegram_username?.toLowerCase() || '').includes(term) ||
        (client.email?.toLowerCase() || '').includes(term)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortBy] || '';
      let bVal = b[sortBy] || '';
      
      if (sortBy === 'created_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredClients(filtered);
  }

  async function loadClientDetails(clientId) {
    setLoadingDetails(true);
    setClientBalance(null);
    setClientTransactions([]);
    
    try {
      // Load balance
      const balanceResponse = await api.get(`/balance/admin/user/${clientId}`);
      setClientBalance(balanceResponse.data);
    } catch (error) {
      console.error('Failed to load balance:', error);
    }

    try {
      // Load transaction history
      const transactionsResponse = await api.get(`/admin/transactions?user_id=${clientId}&limit=10`);
      setClientTransactions(transactionsResponse.data?.transactions || []);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    }

    setLoadingDetails(false);
  }

  async function impersonateUser(clientId) {
    try {
      const res = await api.post(`/admin/impersonate/${clientId}`);
      startImpersonation({
        token: res.data.access_token || res.data.token,
        mode: 'user',
        adminUser: user,
      });
      // Reload app as the impersonated user
      window.location.href = '/';
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || e.message));
    }
  }

  async function updateClientStatus(clientId, status) {
    try {
      await api.post(`/admin/clients/${clientId}/status?status=${status}`);
      setMsg(`✅ Статус изменен на ${status}`);
      loadClients();
      if (selectedClient?.id === clientId) {
        setSelectedClient({...selectedClient, status});
      }
    } catch (error) {
      setMsg('❌ ' + (error.response?.data?.detail || error.message));
    }
  }

  function handleClientSelect(client) {
    setSelectedClient(client);
    loadClientDetails(client.id);
    setMsg('');
  }

  function formatDate(dateString) {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function formatMoney(amount, currency = 'PLN') {
    if (!amount) return '0.00 ' + currency;
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  function getAccountTypeDisplay(accountType) {
    const types = {
      'individual': '👤 Физ. лицо',
      'company': '🏢 Компания',
      'freelancer': '💼 Фрилансер'
    };
    return types[accountType] || accountType;
  }

  function getRoleDisplay(role) {
    const roles = {
      'client': '👤 Клиент',
      'manager': '👔 Менеджер', 
      'admin': '🔧 Админ',
      'superadmin': '👑 Супер-админ'
    };
    return roles[role] || role;
  }

  if (loading) return <Loading />;

  // Client Detail Panel
  if (selectedClient) {
    return (
      <div className="page">
        <BackButton />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => setSelectedClient(null)}
            style={{ padding: '8px 12px', fontSize: 14 }}
          >
            ← Назад к списку
          </button>
          <h1 style={{ margin: 0 }}>
            {selectedClient.first_name} {selectedClient.last_name || ''}
          </h1>
        </div>

        {msg && (
          <div className="card" style={{
            padding: 12,
            marginBottom: 16,
            background: msg.includes('✅') ? 'rgba(0,230,118,0.1)' : 'rgba(255,23,68,0.1)',
            border: `1px solid ${msg.includes('✅') ? 'var(--success)' : 'var(--danger)'}`,
          }}>
            {msg}
          </div>
        )}

        {/* Profile Info Card */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'var(--accent-glow)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20
            }}>
              👤
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 'bold' }}>
                {selectedClient.first_name} {selectedClient.last_name || ''}
              </div>
              <div style={{ color: 'var(--text2)', fontSize: 13 }}>
                @{selectedClient.telegram_username || selectedClient.telegram_id}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text2)' }}>📞 Телефон:</span>
              <span>{selectedClient.phone || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text2)' }}>✉️ Email:</span>
              <span>{selectedClient.email || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text2)' }}>🎭 Роль:</span>
              <span>{getRoleDisplay(selectedClient.role)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text2)' }}>🏷️ Тип аккаунта:</span>
              <span>{getAccountTypeDisplay(selectedClient.account_type)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text2)' }}>📅 Создан:</span>
              <span>{formatDate(selectedClient.created_at)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'var(--text2)' }}>🔘 Статус:</span>
              <span className={`status-badge status-${selectedClient.status}`}>
                {selectedClient.status}
              </span>
            </div>
          </div>
        </div>

        {/* Balance Card */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            💰 Баланс
            {loadingDetails && <span style={{ fontSize: 12 }}>⏳</span>}
          </h3>
          {clientBalance ? (
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text2)' }}>Основной баланс:</span>
                <span style={{ fontSize: 16, fontWeight: 'bold', color: 'var(--success)' }}>
                  {formatMoney(clientBalance.balance)}
                </span>
              </div>
              {clientBalance.bonus_balance > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text2)' }}>Бонусный баланс:</span>
                  <span style={{ fontSize: 14, color: 'var(--warn)' }}>
                    {formatMoney(clientBalance.bonus_balance)}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text2)' }}>Обновлен:</span>
                <span>{formatDate(clientBalance.updated_at)}</span>
              </div>
            </div>
          ) : loadingDetails ? (
            <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 20 }}>
              ⏳ Загрузка баланса...
            </div>
          ) : (
            <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 20 }}>
              Информация о балансе недоступна
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            📊 Последние транзакции
            {loadingDetails && <span style={{ fontSize: 12 }}>⏳</span>}
          </h3>
          {clientTransactions.length > 0 ? (
            <div style={{ display: 'grid', gap: 8 }}>
              {clientTransactions.slice(0, 5).map((transaction, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 8,
                  background: 'var(--bg2)',
                  borderRadius: 6,
                  fontSize: 13
                }}>
                  <div>
                    <div>{transaction.description || 'Транзакция'}</div>
                    <div style={{ color: 'var(--text2)', fontSize: 11 }}>
                      {formatDate(transaction.created_at)}
                    </div>
                  </div>
                  <div style={{
                    color: transaction.amount > 0 ? 'var(--success)' : 'var(--danger)',
                    fontWeight: 'bold'
                  }}>
                    {transaction.amount > 0 ? '+' : ''}{formatMoney(transaction.amount)}
                  </div>
                </div>
              ))}
            </div>
          ) : loadingDetails ? (
            <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 20 }}>
              ⏳ Загрузка транзакций...
            </div>
          ) : (
            <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 20 }}>
              Транзакции отсутствуют
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          <button 
            className="btn btn-primary"
            onClick={() => impersonateUser(selectedClient.id)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            👤 Войти как пользователь
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setMsg('📩 Функция отправки уведомлений в разработке')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            📩 Отправить уведомление
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setMsg('📊 Подробный просмотр транзакций в разработке')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            📊 Транзакции
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setMsg('📄 Просмотр фактур в разработке')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            📄 Фактуры
          </button>
        </div>

        {/* Status Management */}
        <div className="card">
          <h3 style={{ marginBottom: 12 }}>⚙️ Управление статусом</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 8 }}>
            {['active', 'pending', 'blocked', 'suspended'].map(status => (
              <button
                key={status}
                className={`btn btn-small ${selectedClient.status === status ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => updateClientStatus(selectedClient.id, status)}
                disabled={selectedClient.status === status}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Main Client List
  return (
    <div className="page">
      <BackButton />
      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>👥 Клиенты ({filteredClients.length})</h1>
      </div>

      {msg && (
        <div className="card" style={{
          padding: 12,
          marginBottom: 16,
          background: msg.includes('✅') ? 'rgba(0,230,118,0.1)' : 'rgba(255,23,68,0.1)',
          border: `1px solid ${msg.includes('✅') ? 'var(--success)' : 'var(--danger)'}`,
        }}>
          {msg}
        </div>
      )}

      {/* Search and Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gap: 12 }}>
          {/* Search */}
          <div>
            <input
              type="text"
              placeholder="🔍 Поиск по имени, username или email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: '1px solid var(--card-hover)',
                background: 'var(--bg2)',
                color: 'var(--text)',
                fontSize: 14
              }}
            />
          </div>
          
          {/* Sort Controls */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>Сортировка:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid var(--card-hover)',
                background: 'var(--bg2)',
                color: 'var(--text)',
                fontSize: 13
              }}
            >
              <option value="created_at">По дате регистрации</option>
              <option value="first_name">По имени</option>
              <option value="status">По статусу</option>
              <option value="role">По роли</option>
            </select>
            <button
              className="btn btn-small"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              style={{ padding: '6px 8px', fontSize: 12 }}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      {/* Client List */}
      {filteredClients.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text2)' }}>
          {searchTerm ? 'Клиенты не найдены' : 'Нет клиентов'}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {filteredClients.map(client => (
            <div 
              key={client.id} 
              className="card"
              onClick={() => handleClientSelect(client)}
              style={{ 
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                ':hover': { background: 'var(--card-hover)' }
              }}
              onMouseEnter={(e) => e.target.style.background = 'var(--card-hover)'}
              onMouseLeave={(e) => e.target.style.background = 'var(--card)'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Avatar */}
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: 'var(--accent-glow)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  flexShrink: 0
                }}>
                  👤
                </div>
                
                {/* Main Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    fontSize: 15, 
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {client.first_name} {client.last_name || ''}
                  </div>
                  <div style={{ 
                    fontSize: 12, 
                    color: 'var(--text2)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    @{client.telegram_username || client.telegram_id}
                  </div>
                  <div style={{ 
                    fontSize: 11, 
                    color: 'var(--text2)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {client.phone || '—'} • {client.email || '—'}
                  </div>
                </div>
                
                {/* Status Badges */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                  <span className={`status-badge status-${client.status}`} style={{ fontSize: 10 }}>
                    {client.status}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--text2)' }}>
                    {getRoleDisplay(client.role).split(' ')[0]}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--text2)' }}>
                    {getAccountTypeDisplay(client.account_type).split(' ')[0]}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}